import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useGetKioskOrderSummaryQuery } from '../../app/api/api';
import { Spinner } from '../../spinner/spinner';
import styles from './kiosk-order-page.module.css';

export const KioskOrderPage = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [pollingInterval, setPollingInterval] = useState(2000);

  const { data: orderSummary, error, isLoading } = useGetKioskOrderSummaryQuery(
    { orderId: orderId ?? '', token },
    {
      pollingInterval,
      skip: !orderId || !token,
    }
  );

  useEffect(() => {
    if (orderSummary) {
      const hasUnready = orderSummary.items?.downloads.some(
        (d) => !d.ready && d.isInstantDownload
      );
      if (!hasUnready) {
        setPollingInterval(0);
      }
    }
  }, [orderSummary]);

  if (!orderId || !token) {
    return (
      <div className={styles.error}>
        <h1>Invalid Link</h1>
        <p>This download link is missing required information.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner />
      </div>
    );
  }

  if (error) {
    const status = (error as any)?.originalStatus ?? (error as any)?.status;
    return (
      <div className={styles.error}>
        <h1>
          {status === 403
            ? 'Link Expired or Invalid'
            : 'Order Not Found'}
        </h1>
        <p>
          {status === 403
            ? 'This download link has expired or is invalid. Downloads are available for 7 days after purchase.'
            : 'We couldn\'t find this order. Please check the link and try again.'}
        </p>
      </div>
    );
  }

  if (!orderSummary) {
    return null;
  }

  const downloads = orderSummary.items?.downloads ?? [];

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Your Photos</h1>
      <p className={styles.expiry}>Downloads available for 7 days</p>

      <div className={styles.downloadsList}>
        {downloads.map((d) => (
          <div key={String(d._id)} className={styles.item}>
            {d.image.url ? (
              <img
                alt={d.image.name}
                className={styles.img}
                src={d.image.url}
              />
            ) : (
              <div className={styles.placeholder}>{d.image.name}</div>
            )}
            {d.ready ? (
              <a
                className={styles.link}
                download={true}
                href={`${d.downloadUrl}${d.downloadUrl.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`}
              >
                <span>Download</span>
              </a>
            ) : (
              <span className={styles.preparing}>
                <span>Preparing...&nbsp;</span>
                <Spinner size="small" />
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
